import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from "@angular/core";
import {FormControl} from "@angular/forms";
import {CommonService} from "../../../../shared/services/common.service";
import * as moment from "moment/moment";
import {
  coachesFilter,
  sessionDeliveryFilter
} from "../../core/sessions-list.selectors";
import {Store} from "@ngrx/store";
import {Subject, takeUntil} from "rxjs";
import {DropdownOption} from "../../../../../models/dropdown-option.model";
import {Router} from "@angular/router";
import {roles} from "../../../../shared/constants/common.constants";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']
})
export class UserComponent implements OnInit {
  @Input() user: any
  @Input() form: any
  @Input() groupId: number;
  @Input() groupStatus: number;
  @Input() sessionDetails;
  @Input() sessionCompleted: boolean;
  @Output() downloadVisitNoteClicked: EventEmitter<any> = new EventEmitter()

  isOtherCoachSelectionEnabled = false;
  otherCoachesOptionsLoading: boolean = false;
  links = [
    {
      label: 'Behavior Tracker',
      handleClick: (id) => this.handleLinkClick(id, 'behavior-tracker')
    },
    {
      label: 'E-Journal',
      handleClick: (id) => this.handleLinkClick(id, 'my-journal')
    },
    {
      label: 'Measurements',
      handleClick: (id) => this.handleMeasurementsClick(id)
    }
  ]
  groupTypes = {a: 1, ab: 2};
  radioFieldOptionForTypesOfGroup = [
    {
      label: 'Yes',
      value: this.groupTypes.a
    },
    {
      label: 'No',
      value: this.groupTypes.ab
    }
  ];
  errorMessages: any = {};
  errorMessageMap = {
    otherAdults: {
      required: 'This field is required'
    },
    otherChildren: {
      required: 'This field is required'
    },
    otherCoaches: {
      required: 'This field is required'
    }
  };
  coachesOptions = [];
  isCoachSelectionDisabled: boolean = false;
  sessionDeliveryOptions: DropdownOption[] = [];
  otherCoachesOptions: any[] = [];
  sessionFormatOptions = [
    {
      key: 1,
      value: "Group",
    },
    {
      key: 2,
      value: "Individual family",
    },
  ];


  protected readonly roles = roles;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private store: Store,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    if (this.user.length !== 0) {
      this.coachesOptions = this.user?.sessionData?.groupCoaches?.map((item) => ({
        key: item?.id,
        value: item?.name,
        checked: this.user?.sessionData?.coachAttendance?.find((value) => value.id == item.id)
      }))

      if (this.coachesOptions?.length) {
        this.form.controls["coaches"].setValue(this.coachesOptions.filter((item) => item.checked)
          .map((item) => item.key));
      }

      // find other coaches attendance
      const otherCoachAttendance = this.user?.sessionData?.coachAttendance?.filter((item) => !this.user?.sessionData?.groupCoaches?.find((value) => value.id == item.id));
      if (otherCoachAttendance?.length) {
        this.isOtherCoachSelectionEnabled = true;
        this.onSelectOtherCoaches({checked: true}, otherCoachAttendance.map((item) => item.id))
      } else {
        this.isOtherCoachSelectionEnabled = false;
      }

      this.form?.patchValue({
        id: this.user?.id,
        sessionStartDate: this.user?.sessionData?.startDate,
        startTime: this.user?.sessionData?.startDate,
        sessionFormat: this.user?.sessionData?.format,
        sessionDelivery: this.user?.sessionData?.location,
        duration: this.user?.sessionData?.duration,
        internalNote: this.user?.sessionData?.internalNote,
        visitNote: this.user?.sessionData?.visitNote
      }, {emitEvent: true});
      this.form.markAsPristine();
    }

    this.store.select(sessionDeliveryFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.sessionDeliveryOptions = data?.list || [];
        this.cdRef.detectChanges();
      });

    this.store.select(coachesFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.otherCoachesOptionsLoading = data?.loading;
        this.otherCoachesOptions = data?.list?.filter((otherCoach) => {
          return !this.coachesOptions?.find((coach) => coach.key === otherCoach.key);
        }).map((option) => ({
          key: option.key,
          value: option.value,
          disabled: false
        }));
      });


    this.form?.controls["coaches"]?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        // Disable unselected options in otherCoachesOptions
        this.otherCoachesOptions = this.otherCoachesOptions.map(option => ({
          ...option,
        }));
        this.checkCoachSelection();
      });

    if (this.groupStatus !== 2 || this.sessionDetails?.participantStatus?.key === 4 || this.sessionDetails?.participantGroupStatus === 4 || this.sessionCompleted) {
      this.form.disable();
    }

  }

  handleOpenSessionDetailsClick(user) {
    user.isOpen = !user.isOpen
  }

  onCoachSelectionChange(option) {
    option.checked = !option.checked;
    this.form.controls["coaches"].setValue(this.coachesOptions.filter((item) => item.checked)
      .map((item) => item.key));
    this.form.controls['coaches'].markAsDirty();
  }

  onSelectOtherCoaches(event, defaultValues = null) {
    if (event?.checked) {
      this.form.addControl(
        "otherCoaches",
        new FormControl({value: defaultValues, disabled: false})
      );

      this.form.controls["otherCoaches"].valueChanges.pipe(takeUntil(this.onDestroy))
        .subscribe((data) => {
          this.checkCoachSelection();
        });
    } else {
      this.form.removeControl("otherCoaches");
    }
    this.checkCoachSelection();
  }

  checkCoachSelection() {
    const defaultCoachCount = +this.form.controls["coaches"].value?.length;
    const otherCoachCount = +this.form.controls["otherCoaches"]?.value?.length;
    this.isCoachSelectionDisabled = (defaultCoachCount + otherCoachCount) === 6;

    // Disable unselected options in otherCoachesOptions
    if (this.form.controls["otherCoaches"]) {
      this.otherCoachesOptions = this.otherCoachesOptions.map(option => ({
        ...option,
        disabled: this.isCoachSelectionDisabled && !this.form.controls["otherCoaches"]?.value.includes(option.key),
      }));
    }
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  handleLinkClick(id, path: string) {
    const urlPrefix = this.router.url.split('/')[1];
    window.open(`/${urlPrefix}/member/${id}/${path}`, '_blank');
  }

  handleMeasurementsClick(data) {
    const urlPrefix = this.router.url.split('/')[1];
    const url = new URL('http://localhost');
    url.searchParams.append('groupId', '' + this.groupId);
    url.searchParams.append('date', this.sessionDetails.sessionData.startDate);
    window.open(`/${urlPrefix}/member/${data}/metrics${url.search}`, '_blank')
  }

  handleDownloadVisitNotesClick(data) {
    this.downloadVisitNoteClicked.emit(data)
  }
}
